import { client } from "@/hono";
import { Button } from "@msuite/ui/button";
import { icon, Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import type { Calculation } from "@msuite/utils/types";
import React from "react";
import { useNavigate } from "react-router";
import { useCitadel } from "@/hooks/use-citadel";

export function RowActions({ cell }: CellContext<Calculation, "actions">) {
	/** Hooks */
	const { refetch } = useDataTableContext();
	const calculationId = cell.row.original.id;
	const { t } = useTranslation();
	const asyncModal = useAsyncModal();
	const navigate = useNavigate();

	/** Query */
	const { citadel } = useCitadel();

	/** Functions */
	async function handleOnDelete() {
		try {
			if (cell.row.original.state === "locked") return;
			const isConfirmed = await asyncModal.confirm();
			if (!isConfirmed) return;
			await client.calculations[":calculationId"].$delete({
				param: { calculationId },
			});
			await refetch?.();
		} catch (error) {
			console.error(error);
		}
	}

	function handleOnNavigateToEdit() {
		navigate(
			`/calculations/${calculationId}/editor?returnTo=${encodeURIComponent("/calculations")}`,
		);
	}

	async function handleOnConvertToProject() {
		toast.promise(
			async () => {
				const response = await client.calculations.offers[":calculationId"].convert.$post({
					param: { calculationId },
				});
				const data = await responseHandler(response);
				if (!data.project?.id) {
					toast.error(t("calculations.calculation_was_alreay_converted"));
					return;
				}
				navigate(`/projects/${data.project?.id}`);
			},
			{
				loading: t("general.converting"),
				error: t("general.error"),
				success: t("general.done"),
			},
		);
	}

	/** Computed */
	// Converting does two things and the route gates both: it edits the calculation, which reaches
	// the whole organisation or only what the caller wrote, and it creates a project, which the
	// base set grants nobody. So the caller's own calculation is not enough — without
	// `create Project` the entry leads to a refusal every time. Three states rather than two: the
	// entry disappears on a definitive denial only, the same guard `Can` documents.
	const convertIsDenied =
		!!citadel &&
		(citadel.cannot("update", "Calculation", { ...cell.row.original }) ||
			citadel.cannot("create", "Project"));

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={`/calculations/${calculationId}`}>
				<Menu
					items={createStandardMenuItems({
						onEdit: handleOnNavigateToEdit,
						onDelete: handleOnDelete,
						items: [
							convertIsDenied
								? null
								: {
										label: t("calculations.convert_to_project"),
										isDisabled:
											cell.row.original.type !== "offer" ||
											!!cell.row.original._serviceCalculationMeta?.projectId,
										onClick: handleOnConvertToProject,
										icon: icon("ChevronsRight"),
									},
						],
					})}
				>
					<Button
						size="xxs"
						icon={<Icon name="EllipsisVertical" />}
						variant="ghost"
						data-cy="row-actions-button"
					/>
				</Menu>
			</RowActionsWrapper>
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					message={t("calculations.delete_calculation_confirm_message")}
				/>
			)}
		</React.Fragment>
	);
}
